import { ApiClientInterface } from './clients';
import { HttpError } from './errors';
import {
  Investment,
  InvestmentPackage,
  InvestmentPackageSettings,
  Money,
  PackageStats,
} from './models';
import {
  normalizeInvestmentPackage,
  normalizeInvestmentPackageSettings,
  normalizePackageStats,
} from './models';

export class InvestmentPackageManager {
  constructor(private readonly apiClient: ApiClientInterface) {}

  public async getSettings(): Promise<InvestmentPackageSettings> {
    const data = await this.apiClient.get('/settings');
    const settings = normalizeInvestmentPackageSettings(data);

    if (!settings) {
      throw new HttpError('Invalid investment package settings response');
    }

    return settings;
  }

  public async getAll(): Promise<InvestmentPackage[]> {
    const data = await this.apiClient.get('/');
    if (!Array.isArray(data)) {
      return [];
    }

    return data
      .map(normalizeInvestmentPackage)
      .filter((pkg): pkg is InvestmentPackage => pkg !== null);
  }

  public async getPackageStats(): Promise<PackageStats[]> {
    const data = await this.apiClient.get('/stats');
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map(normalizePackageStats).filter((stat): stat is PackageStats => stat !== null);
  }

  public async getOne(packageUuid: string): Promise<InvestmentPackage> {
    const data = await this.apiClient.get(`/${packageUuid}`);
    const pkg = normalizeInvestmentPackage(data);

    if (!pkg) {
      throw new HttpError('Invalid investment package response');
    }

    return pkg;
  }

  public getInvestments(packageUuid: string): Promise<Investment[]> {
    return this.apiClient.get(`/${packageUuid}/investments`);
  }

  public create(
    money: Money,
    dealsCount: number,
    enableCapitalProtection = false
  ): Promise<InvestmentPackage> {
    return this.apiClient.post('/', { money, dealsCount, enableCapitalProtection });
  }
}
